/**
 * Fetch a painted deck before a card asks for it.
 *
 * Switching theme changes the `src` of every card on screen at once, and a
 * browser shows the OLD picture until the new one arrives - so a table
 * repainted card by card as each file landed, over a couple of seconds, which
 * reads as the app struggling rather than as a deck being dealt.
 *
 * The real cost is round trips rather than bytes: thirty cards, each its own
 * request. Caching fixed the second switch and every one after it (see the
 * Caddyfile); this is for the first, when the files genuinely are not there
 * yet.
 *
 * ## Why `prefetch` and not `preload`
 *
 * `preload` says "I need this for the current page, get it now", and would put
 * fifty cards in front of the ones actually on screen. `prefetch` is the idle
 * priority: the browser fetches them when it has nothing better to do, which
 * is exactly the right bargain for art that MIGHT be turned over next.
 *
 * ## Why the whole deck
 *
 * Half a megabyte, once per theme, then never again. The alternative is
 * guessing which cards come next, and a card game's answer to that is "any of
 * them" - a shuffle has no favourites. Fetching the ones already on screen
 * would help nothing, because those are the requests that are already in
 * flight.
 */

import { useEffect } from 'react';
import { faceImage } from './skin.js';
import type { FaceSet } from './skin.js';

const RANKS = ['A', '2', '3', '4', '5', '6', '7', '8', '9', 'T', 'J', 'Q', 'K'];
const SUITS = ['S', 'H', 'D', 'C'];
const JOKERS = ['JR', 'JB'];

/** Decks already asked for, so a second table does not queue them again. */
const fetched = new Set<string>();

export function useDeckPrefetch(faces: FaceSet): void {
  const base = faces.kind === 'cards' ? faces.base : null;

  useEffect(() => {
    if (base === null || typeof document === 'undefined') return undefined;
    if (fetched.has(base)) return undefined;
    fetched.add(base);

    const links: HTMLLinkElement[] = [];
    for (const card of [...SUITS.flatMap((s) => RANKS.map((r) => r + s)), ...JOKERS]) {
      const href = faceImage(faces, card);
      if (href === null) continue;
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.as = 'image';
      link.href = href;
      document.head.appendChild(link);
      links.push(link);
    }

    /*
     * The tags go when the table does, not the cache.
     *
     * A `<link>` is an instruction rather than a store - once the browser has
     * acted on it the bytes live in the HTTP cache, and leaving fifty dead
     * tags in the head of a long session helps nobody.
     */
    return () => {
      for (const link of links) link.remove();
    };
    // `faces` is rebuilt whenever the skin object is, and the skin object is
    // rebuilt on a packet; keying on the base path is what makes this run once
    // per deck rather than once per tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base]);
}
